import {
  Box,
  BoxProps,
  Checkbox,
  Chip,
  styled,
  Typography,
  useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { IObject } from 'src/models/objectManagement';
import BlibliIcon from '../BlibliIcon';
import ButtonCircle from '../ButtonCircle';
import LazadaIcon from '../LazadaIcon';
import { LightTooltip } from '../ObjectPreview';
import OlxIcon from '../OlxIcon';
import ShopeeIcon from '../ShopeeIcon';
import TiktokIcon from '../TikTokIcon/TikTokIcon';
import TokopediaIcon from '../TokopediaIcon';
import CheckIcon from '@mui/icons-material/Check';
import useDebounceCallback from 'src/hooks/useDebounceCallback';
import { reducerUpdateAnalyticArgumentObjectIdList } from 'src/features/analyticArguments';
import { ECOMMERCE_SOURCES } from 'src/constants';

const Container = styled(Box)<BoxProps>(
  ({ theme }) => `
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 8px 11px 8px 11px;
    border-radius:18px;
    background: #FBFBFB;
    border: 1px solid #E8E9EA;
    box-sizing: border-box;
    flex-wrap: wrap;
  `
);

const CheckboxCustom = styled(Checkbox)(
  ({ theme }) => `
    &:hover {
      background-color: unset;
    }
    padding: 0
  `
);

interface ColorChip {
  colorChip: string;
}

const ChipCustom = styled(Chip)<ColorChip>(
  ({ theme, colorChip }) => `
    margin-right: ${theme.spacing(1)};
    background-color: ${theme.palette.primary.main};
    border-radius: ${theme.spacing(1.43)};
    padding: ${theme.spacing(1)} 0px ${theme.spacing(1)} 2px;
    width: ${theme.spacing(7)};
    height: ${theme.spacing(3)};
    cursor: pointer;
    &:hover {
      background-color: ${theme.palette.primary.dark};
    }
    .MuiChip-label {
        color: ${colorChip};
        font-size: 13px;
        line-height: 130%;
        margin: 8px 0px 8px 8px;
    }
    .MuiChip-avatarSmall {
        width: 18px;
        color: ${colorChip};
        margin: 0px -3px 0px 0px;
    }
    .MuiChip-deleteIcon {
        font-size: 14px;
        color: ${colorChip};
    }
  `
);

interface IProps {
  objectList: Array<IObject>;
}

export type TEcommerce = typeof ECOMMERCE_SOURCES[number];

const initSelectedSources = (): Record<TEcommerce, boolean> => ({
  lazada: true,
  tokopedia: true,
  shopee: true,
  blibli: true
});

const sources: {
  key: TEcommerce;
  bgColor: string;
  icon: React.ReactElement;
}[] = [
  {
    key: 'tokopedia',
    bgColor: '#42B549',
    icon: <TokopediaIcon sx={{ color: '#fbfbfb', fontSize: 16 }} />
  },
  {
    key: 'shopee',
    bgColor: '#EE4D2D',
    icon: <ShopeeIcon sx={{ color: '#fbfbfb', fontSize: 16 }} />
  },
  {
    key: 'blibli',
    bgColor: '#56B6E9',
    icon: <BlibliIcon sx={{ color: '#fbfbfb', fontSize: 16 }} />
  },
  // {
  //   key: 'olx',
  //   bgColor: '#3A77FF',
  //   icon: <OlxIcon sx={{ color: '#fbfbfb', fontSize: 16 }} />
  // },
  {
    key: 'lazada',
    bgColor: '#070286',
    icon: <LazadaIcon sx={{ color: '#fbfbfb', fontSize: 16 }} />
  }
  // {
  //   key: 'tiktokshop',
  //   bgColor: '#051C26',
  //   icon: <TiktokIcon sx={{ color: '#fbfbfb', fontSize: 16 }} />
  // }
];

const EcommerceObjectPreview = ({ objectList }: IProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const [selectedSources, setSelectedSources] = useState<
    Record<TEcommerce, boolean>
  >(initSelectedSources());

  const { objectListByObjectType } = useAppSelector(
    (state) => state.objectManagement
  );

  const getObjectCount = (key: TEcommerce) => {
    return objectList.filter((obj) => {
      const isObjMatch = obj.socialMedia === key;
      return isObjMatch;
    }).length;
  };

  const getActiveObjectIds = (
    currentSelectedSources: Record<TEcommerce, boolean>
  ): string[] => {
    return objectList.reduce<string[]>((acc, obj) => {
      if (currentSelectedSources[obj.socialMedia as TEcommerce]) {
        acc.push(obj.id);
      }
      return acc;
    }, []);
  };

  const debouncedUpdateObjectList = useDebounceCallback(
    (updatedSelectedSources: Record<TEcommerce, boolean>) => {
      const activeObjectIds = getActiveObjectIds(updatedSelectedSources);
      dispatch(reducerUpdateAnalyticArgumentObjectIdList(activeObjectIds));
    },
    1000
  );

  const handleClickSource = (key: TEcommerce) => {
    const updatedSelectedSources = {
      ...selectedSources,
      [key]: !selectedSources[key]
    };
    setSelectedSources(updatedSelectedSources);
    debouncedUpdateObjectList(updatedSelectedSources);
  };

  const isSourceDisabled = (count: number) => count <= 0;

  const handleClickAll = () => {
    const allSelected = initSelectedSources();
    setSelectedSources(allSelected);

    const objectIdList = objectList.map((object) => {
      return object.id;
    });
    dispatch(reducerUpdateAnalyticArgumentObjectIdList(objectIdList));
  };

  useEffect(() => {
    setSelectedSources({
      tokopedia: objectListByObjectType.some(
        (item) => item.socialMedia === 'tokopedia'
      ),
      blibli: objectListByObjectType.some(
        (item) => item.socialMedia === 'blibli'
      ),
      lazada: objectListByObjectType.some(
        (item) => item.socialMedia === 'lazada'
      ),
      shopee: objectListByObjectType.some(
        (item) => item.socialMedia === 'shopee'
      )
    });
  }, [objectListByObjectType]);

  return (
    <Container
      alignItems={{ xs: 'left', md: 'center' }}
      justifyContent={{ xs: 'left' }}
    >
      <ButtonCircle
        onClick={handleClickAll}
        sx={{
          marginRight: theme.spacing(2),
          background: '#E0F6FF'
        }}
      >
        <LightTooltip
          title={objectList
            .map((object) => {
              return object.objectName;
            })
            .join(', ')}
        >
          <Typography
            sx={{ fontSize: '14px', color: '#009FE3', fontWeight: 600 }}
          >
            All
          </Typography>
        </LightTooltip>
      </ButtonCircle>

      {sources.map((source) => {
        const count = getObjectCount(source.key);
        const isDisabled = isSourceDisabled(count);
        const isChecked = selectedSources[source.key];

        return (
          <CheckboxCustom
            key={source.key}
            onClick={() => handleClickSource(source.key)}
            checked={isChecked}
            disabled={isDisabled}
            icon={
              <ChipCustom
                variant="filled"
                deleteIcon={<CheckIcon />}
                avatar={source.icon}
                label={count}
                size="small"
                sx={{
                  background: '#DBDBDB'
                }}
                colorChip={theme.palette.common.white}
              />
            }
            checkedIcon={
              <ChipCustom
                variant="filled"
                deleteIcon={<CheckIcon />}
                avatar={source.icon}
                label={count}
                size="small"
                sx={{
                  background: isDisabled ? '#DBDBDB' : source.bgColor
                }}
                colorChip={theme.palette.common.white}
              />
            }
          />
        );
      })}
    </Container>
  );
};

export default EcommerceObjectPreview;
